import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import type { Consumer } from "@rails/actioncable"
import { useActionCable, type CableStatus } from "@/hooks/useActionCable"
import { chatReducer, initialChatState } from "@/hooks/useChat"
import { postJson, type ApiError } from "@/lib/api"
import {
  ARCHIE_GREETINGS,
  buildArchieClientContext,
  captureArchieEvent,
  fieldTestQueryOverride,
  localizedCopy,
} from "@/lib/archieContext"
import { readArchieSession, writeArchieSession } from "@/lib/archieSession"
import type { ChatMessage, StreamEvent } from "@/types/chat"

interface ArchieCreateResponse {
  chat_id: number
  guest: boolean
  locale: string
  messages: ChatMessage[]
  interview_status?: string
}

type ArchieAction =
  | { type: "hydrate"; messages: ChatMessage[]; preserveStreaming?: boolean }
  | Parameters<typeof chatReducer>[1]

function archieReducer(state: typeof initialChatState, action: ArchieAction) {
  if (action.type === "hydrate") {
    return {
      ...initialChatState,
      messages: action.messages,
      isStreaming: action.preserveStreaming ? state.isStreaming : false,
      error: null,
    }
  }
  return chatReducer(state, action)
}

const CABLE_WAIT_MS = 5_000
const STREAM_CATCHUP_MS = 4_000

function waitForCable(
  getStatus: () => CableStatus,
  timeoutMs = CABLE_WAIT_MS
): Promise<boolean> {
  const initial = getStatus()
  if (initial === "connected") return Promise.resolve(true)
  if (initial === "rejected") return Promise.resolve(false)

  return new Promise((resolve) => {
    const started = Date.now()
    const tick = () => {
      const status = getStatus()
      if (status === "connected") {
        resolve(true)
        return
      }
      if (status === "rejected" || Date.now() - started >= timeoutMs) {
        resolve(false)
        return
      }
      window.setTimeout(tick, 40)
    }
    tick()
  })
}

export interface UseArchieChatResult {
  open: boolean
  setOpen: (open: boolean) => void
  locale: string
  ready: boolean
  loading: boolean
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  ensureChat: () => Promise<number | null>
  sendMessage: (content: string) => Promise<void>
}

export interface UseArchieChatOptions {
  /** Injectable for tests. */
  consumer?: Consumer
}

export function useArchieChat(options: UseArchieChatOptions = {}): UseArchieChatResult {
  const snapshot = readArchieSession()
  const [open, setOpenState] = useState(snapshot.open)
  const [chatId, setChatIdState] = useState<number | null>(snapshot.chatId)
  const [locale, setLocaleState] = useState(snapshot.locale)
  const [loading, setLoading] = useState(false)
  const [state, dispatch] = useReducer(archieReducer, snapshot.state)
  const cableStatusRef = useRef<CableStatus>(snapshot.chatId ? "connecting" : "idle")
  const isStreamingRef = useRef(state.isStreaming)
  const catchupTimerRef = useRef<number | null>(null)
  const bootRef = useRef({ chatId: snapshot.chatId, streaming: snapshot.state.isStreaming })
  const refreshRef = useRef<(preserveStreaming?: boolean) => Promise<number | null>>(
    async () => null
  )

  isStreamingRef.current = state.isStreaming

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next)
    writeArchieSession({ open: next })
  }, [])

  const setChatId = useCallback((next: number | null) => {
    setChatIdState(next)
    writeArchieSession({ chatId: next })
  }, [])

  const setLocale = useCallback((next: string) => {
    setLocaleState(next)
    writeArchieSession({ locale: next })
  }, [])

  useEffect(() => {
    writeArchieSession({ state })
  }, [state])

  useActionCable({
    chatId: chatId ?? 0,
    consumer: options.consumer,
    onEvent: useCallback((event: StreamEvent) => {
      dispatch({ type: "event", event })
      if (event.type === "complete" || event.type === "error") {
        if (catchupTimerRef.current != null) {
          window.clearTimeout(catchupTimerRef.current)
          catchupTimerRef.current = null
        }
      }
    }, []),
    onStatus: useCallback((status: CableStatus) => {
      cableStatusRef.current = status
    }, []),
  })

  const refreshMessages = useCallback(async (preserveStreaming = false) => {
    try {
      const context = buildArchieClientContext()
      const fieldTest = fieldTestQueryOverride()
      const data = await postJson<ArchieCreateResponse>(
        "/api/archie/chats",
        fieldTest ? { ...context, field_test: fieldTest } : context
      )
      setChatId(data.chat_id)
      if (data.locale) setLocale(data.locale || "en")
      const serverMessages = Array.isArray(data.messages) ? data.messages : []
      if (serverMessages.length > 0) {
        dispatch({ type: "hydrate", messages: serverMessages, preserveStreaming })
      }
      return data.chat_id
    } catch {
      return null
    }
  }, [setChatId, setLocale])

  refreshRef.current = refreshMessages

  // After a Turbo shell remount, restore live history from the server so any
  // tokens missed while the cable subscription was down still appear.
  useEffect(() => {
    const boot = bootRef.current
    if (!boot.chatId) return
    void refreshRef.current(boot.streaming)
  }, [])

  const ensureChat = useCallback(async (): Promise<number | null> => {
    if (chatId) {
      await waitForCable(() => cableStatusRef.current)
      return chatId
    }
    setLoading(true)
    try {
      const context = buildArchieClientContext()
      const fieldTest = fieldTestQueryOverride()
      const data = await postJson<ArchieCreateResponse>(
        "/api/archie/chats",
        fieldTest ? { ...context, field_test: fieldTest } : context
      )
      const nextLocale = data.locale || context.locale || "en"
      setChatId(data.chat_id)
      setLocale(nextLocale)
      // Expect useActionCable to move connecting → connected after commit.
      cableStatusRef.current = "connecting"

      const serverMessages = Array.isArray(data.messages) ? data.messages : []
      if (serverMessages.length === 0) {
        dispatch({
          type: "hydrate",
          messages: [
            {
              id: -100,
              role: "assistant",
              content: localizedCopy(ARCHIE_GREETINGS, nextLocale),
              created_at: new Date().toISOString(),
            },
          ],
        })
      } else {
        dispatch({ type: "hydrate", messages: serverMessages })
      }

      captureArchieEvent("archie_chat_created", { chat_id: data.chat_id, guest: data.guest })

      // chatId state updates async; wait until ActionCable reports connected so
      // the first message does not race the subscription handshake.
      await waitForCable(() => cableStatusRef.current)
      return data.chat_id
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not start Archie"
      dispatch({ type: "send_failed", error: message })
      return null
    } finally {
      setLoading(false)
    }
  }, [chatId, setChatId, setLocale])

  const scheduleCatchup = useCallback(() => {
    if (catchupTimerRef.current != null) {
      window.clearTimeout(catchupTimerRef.current)
    }
    catchupTimerRef.current = window.setTimeout(() => {
      catchupTimerRef.current = null
      if (!isStreamingRef.current) return
      void refreshMessages(false)
    }, STREAM_CATCHUP_MS)
  }, [refreshMessages])

  useEffect(() => {
    return () => {
      if (catchupTimerRef.current != null) {
        window.clearTimeout(catchupTimerRef.current)
      }
    }
  }, [])

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim()
      if (!trimmed) return

      const ensuredId = chatId ?? (await ensureChat())
      if (!ensuredId) return

      // ensureChat waits when it creates; when chatId was already set, still
      // gate on cable so Turbo remounts cannot post before resubscribe.
      const ready = await waitForCable(() => cableStatusRef.current)
      if (!ready && cableStatusRef.current === "rejected") {
        dispatch({
          type: "send_failed",
          error: "Live updates unavailable. Try refreshing the page.",
        })
        return
      }

      const optimistic: ChatMessage = {
        id: Date.now() * -1,
        role: "user",
        content: trimmed,
        created_at: new Date().toISOString(),
      }
      dispatch({ type: "send", message: optimistic })
      captureArchieEvent("archie_message_sent", { chat_id: ensuredId })

      try {
        await postJson<{ message_id: number }>(`/api/archie/chats/${ensuredId}/messages`, {
          content: trimmed,
        })
        scheduleCatchup()
      } catch (err) {
        const apiError = err as ApiError
        dispatch({
          type: "send_failed",
          error: apiError.message || "Failed to send message",
        })
      }
    },
    [chatId, ensureChat, scheduleCatchup]
  )

  return {
    open,
    setOpen,
    locale,
    ready: chatId != null,
    loading,
    messages: state.messages,
    isStreaming: state.isStreaming,
    error: state.error,
    ensureChat,
    sendMessage,
  }
}
